import { MRT_ColumnDef } from "material-react-table";
import { Box, Link, Typography } from "@mui/material";
import CustomTable from "../../components/custom-table/CustomTable";
import CustomLinearProgressBar from "../../components/custom-linear-progress-bar/CustomLinearProgressBar";
import {
  MANAGE_PATIENT_SEARCH_PLACEHOLDER,
  MANAGE_PATIENTS_TABLE_HEADER,
} from "../../utility/constants/locale";
import { useAppSelector } from "../../store/hooks";
import { useGetPatientListQuery } from "./GetPatientListAPISlice";
import { formatDate } from "../../utility/functions/common-functions";
import { useNavigate } from "react-router-dom";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";

interface Patient {
  patientCaseId: number;
  mrnid: string;
  preferredDisplayName: string;
  patientEnrolledDate: string | null;
  initialDeliveryDueDate: string;
  statusDisplayName: string;
  currentPregnancyWeek: number;
  displayGroupDisplayName: string;
  groupCompletionPercentage: number;
  currentTier: string;
  earnedPoints: number;
  totalRewardPoints: number;
}

const ManagePatients: React.FC = () => {
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken ?? null
  );
  const {
    data: patients,
    error,
    isLoading,
  } = useGetPatientListQuery({ accessToken });

  const columns: MRT_ColumnDef<Patient>[] = [
    {
      accessorKey: "mrnid",
      header: "MRN No.",
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "preferredDisplayName",
      header: "Patient Name",
      Cell: ({ row }) => {
        const navigate = useNavigate();
        const handleClick = () => {
          navigate(`/admin/edit-patient-details/${row.original.patientCaseId}`);
        };
        return (
          <Link
            onClick={handleClick}
            sx={{
              fontWeight: 500,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {row.original.preferredDisplayName}
          </Link>
        );
      },
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "patientEnrolledDate",
      header: "Enrollment Date",
      Cell: ({ cell }) => formatDate(cell.getValue<string | null>()),
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "initialDeliveryDueDate",
      header: "Due Date",
      Cell: ({ cell }) => formatDate(cell.getValue<string | null>()),
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "statusDisplayName",
      header: "Patient Status",
      size: 180,
      filterFn: "contains",
    },
    {
      accessorKey: "displayGroupDisplayName",
      header: "Current Week Group",
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "currentTier",
      header: "Tier",
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "earnedPoints",
      header: "Points Earned",
      Cell: ({ row }) => {
        const earned = row.original.earnedPoints;
        const total = row.original.totalRewardPoints;
        return (
          <Box display="flex" alignItems="center">
            <Typography variant="body2" sx={{ color: "#CF489A" }}>
              {earned}
            </Typography>
            <Typography variant="body2">{`/${total}`}</Typography>
          </Box>
        );
      },
      size: 150,
      filterFn: "contains",
    },
    {
      accessorKey: "groupCompletionPercentage",
      header: "Milestone Progress",
      Cell: ({ renderedCellValue }) => (
        <CustomLinearProgressBar value={Number(renderedCellValue)} />
      ),
      size: 180,
      filterFn: "contains",
    },
  ];

  if (isLoading) return <CustomSpinner />;
  if (error)
    return (
      <div className="error">Failed to load data. Please try again later.</div>
    );

  return (
    <Box className="managePatients">
      <CustomTable
        data={patients.managePatientsOverview}
        columns={columns}
        title={MANAGE_PATIENTS_TABLE_HEADER}
        totalRecords={patients.managePatientsOverview.length}
        globalFilterPlaceholder={MANAGE_PATIENT_SEARCH_PLACEHOLDER}
      />
    </Box>
  );
};

export default ManagePatients;
